import React, { memo, useMemo } from 'react';
import { buildConnectivityStrip, connectionStateAt } from '../utils/dayConnectivity';
import { formatInTimeZone, getBrowserTimeZone } from '../utils/timezone';
import './DayConnectivityStrip.css';

const STATE_LABELS = {
  connected: 'Connected',
  failing: 'Failing to connect',
  disconnected: 'Not connected'
};

const fmtTime = (ms) => formatInTimeZone(ms, getBrowserTimeZone(), { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });

const spanStyle = ({ left, width }) => ({ left: `${left}%`, width: `${width}%` });

/** Separate from the playhead so playback doesn't re-render every segment each tick. */
const StripSegments = memo(({ strip }) => (
  <>
    {strip.connections.map((c) => (
      <div
        key={`connection-${c.startMs}`}
        className="day-connectivity-segment connected"
        style={spanStyle(c)}
        title={`Connected ${fmtTime(c.startMs)} – ${fmtTime(c.endMs)}`}
      />
    ))}
    {strip.failures.map((f) => (
      <div
        key={`failure-${f.startMs}`}
        className="day-connectivity-segment failing"
        style={spanStyle(f)}
        title={`Failing to connect ${fmtTime(f.startMs)} – ${fmtTime(f.endMs)} · ${f.item.failureCount} attempts`}
      />
    ))}
    {strip.otaAttempts.map((a) => (
      <div
        key={`ota-${a.startMs}`}
        className={`day-connectivity-ota ${a.item.outcome}`}
        style={spanStyle(a)}
        title={`OTA update ${fmtTime(a.startMs)} · ${a.item.outcome.replace('_', ' ')}`}
      />
    ))}
    {strip.criticalEvents.map((e) => (
      <div
        key={`critical-${e.startMs}-${e.item.eventName}`}
        className="day-connectivity-critical"
        style={{ left: `${e.left}%` }}
        title={`${e.item.eventName} · ${fmtTime(e.startMs)}`}
      />
    ))}
  </>
));

/**
 * The day's goggles connection, lined up with the playback slider underneath
 * it: both run from the first to the last recorded point.
 *
 * @param {Object} data - from deriveGogglesConnectivityTimeline
 * @param {number} trackStartMs - First recorded point
 * @param {number} trackEndMs - Last recorded point
 * @param {number} currentMs - Playback position
 */
const DayConnectivityStrip = ({ data, trackStartMs, trackEndMs, currentMs }) => {
  const strip = useMemo(() => buildConnectivityStrip(data, trackStartMs, trackEndMs), [data, trackStartMs, trackEndMs]);
  const state = connectionStateAt(data, currentMs);
  const playhead = ((currentMs - trackStartMs) / Math.max(1, trackEndMs - trackStartMs)) * 100;

  return (
    <div className="day-connectivity">
      <div className="day-connectivity-head">
        <span>Goggles</span>
        <span className={`day-connectivity-state ${state}`}>{STATE_LABELS[state]}</span>
      </div>
      <div className="day-connectivity-track">
        <StripSegments strip={strip} />
        <div className="day-connectivity-playhead" style={{ left: `${playhead}%` }} />
      </div>
    </div>
  );
};

export default DayConnectivityStrip;
